/* istanbul ignore file */
import { autorun, action, observable, computed, toJS } from 'mobx';
import { fromPromise } from 'mobx-utils';
import { create, persist } from 'mobx-persist';
import fetch from '@elevio/elevio-frontend-utils/dist/fetch';
import {
  memoize,
  mapValues,
  every,
  values,
  isEqual,
  isEmpty,
  cloneDeep,
} from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { reportError } from '@elevio/elevio-frontend-utils/dist/diagnostics';
import {
  validateEmail,
  requiredFieldIsEmpty,
  authQuery,
  trackTicketSubmit,
  isStorageSupported,
} from '../utils/index';
import { debugInfo } from '../utils/debug';
import { defaultTexts, API_URL, RESOURCES } from '../config';
import UploaderStore from './uploader';
import SettingsStore from './settings';

const defaultForm = {
  id: '',
  title: '',
  fields: [],
};

const defaultNonce = {
  token: '',
};

const emptyPromise = s => new Promise(resolve => resolve(s));

const validateData = (
  { type, response, required, id },
  { getFiles },
  // eslint-disable-next-line
  { required: required_field, valid_email }
) => {
  // required for hidden might be accidentally set to true in the form
  if (type === 'hidden') {
    return {
      isValidField: true,
      message: '',
    };
  }
  if (required) {
    if (type === 'upload') {
      const fileLocations = getFiles(id).fileList.map(file => file.location);
      return {
        isValidField: fileLocations.length !== 0,
        message: required_field,
      };
    }
    if (type === 'rate') {
      return {
        isValidField: !!(response > 0),
        message: required_field,
      };
    }
    if (type === 'email') {
      return {
        isValidField: validateEmail(response),
        message: valid_email,
      };
    }
    // input, textarea, checkbox, radio, select
    return {
      isValidField: response.length !== 0,
      message: required_field,
    };
  }
  // if email field is not required then only validate if email response is provided
  if (type === 'email') {
    return {
      isValidField: response.length ? validateEmail(response) : true,
      message: valid_email,
    };
  }
  return {
    isValidField: true,
    message: '',
  };
};

const INITIAL_STORAGE_VALUES = '{"formValues":{}}';

class Form {
  @observable
  saveClicked = false;
  @observable
  submitClicked = false;
  @observable
  articleHelped = false;
  @observable
  sent = false;
  @observable
  saveAttempt = 0;
  @observable
  autorunForSaveAttempt = 0;
  @observable
  uploaderStoreCreated = false;
  inputs = observable.map();
  @observable
  uploaderStore;
  @observable
  fetching = undefined;
  @observable
  suggestions = [];
  @observable
  suggestionsError = null;
  @observable
  accountId = null;
  @observable
  searchKeywords = [];
  @observable
  ticketUid = undefined;
  @observable
  searchedGatekeeperValues = {};
  @observable
  gatekeeperValues = {};
  @observable
  oldGatekeeperValues = {};
  @persist
  @observable
  storedValuesString = INITIAL_STORAGE_VALUES;

  constructor(settingsStore, request, uploaderStore) {
    this.settingsStore = settingsStore;
    this.uploaderStore = uploaderStore;
    this.request = request;
    this.settingsStore.formStore = this;
    this.updateUiOnSave();
    autorun(() => {
      if (settingsStore.isLoading || this.uploaderStoreCreated) return;
      if (this.formPromise.state === 'fulfilled') {
        this.form.fields.map(
          f =>
            f.type === 'upload' &&
            this.uploaderStore.createFiles &&
            this.uploaderStore.createFiles(f)
        );
        this.uploaderStoreCreated = true;
      }
    });
    autorun(() => {
      if (settingsStore.isLoading) return;
      if (this.formPromise.state === 'rejected') {
        reportError('Get form', this.formPromise.value);
      }
    });
    autorun(() => {
      if (settingsStore.isLoading) return;
      if (this.savePromise.state === 'rejected') {
        reportError('Save form', this.savePromise.value);
      }
    });
    autorun(() => {
      if (settingsStore.isLoading) return;
      // when save attempt is incremented, and nonce is available, proceed with saving
      if (this.canSave) {
        this.cacheSave(
          this.settingsStore.cuid,
          this.settingsStore.id,
          this.settingsStore.user.userHash
        );
      }
    });
    autorun(() => {
      if (settingsStore.isLoading) return;
      // sync gatekeeper values with session / json form values
      if (this.formPromise.state === 'fulfilled') {
        this.loadGatekeeperValuesFromSession();
      }
    });
  }

  storage = () =>
    new Promise(resolve => {
      this.resolveStorage = resolve;
    });

  @action
  resolveStorage = data => {
    this.storage = data;
  };

  // canFetchNonce is truthy when save has been clicked
  @computed
  get canFetchNonce() {
    const canFetch = this.saveClicked && !this.attemptsMatch;
    return canFetch;
  }

  // canSave is truthy when nonce is available
  @computed
  get canSave() {
    const noncePromiseState = this.noncePromise.state === 'fulfilled';
    const { nonce, canFetchNonce } = this;
    const nonceToken = this.nonce && this.nonce.token;

    const isAbleToSave = !!(
      noncePromiseState &&
      nonce &&
      nonceToken &&
      canFetchNonce
    );

    return isAbleToSave;
  }

  cacheFormPromise = memoize((
    cuid,
    id,
    language,
    nonce // eslint-disable-line
  ) =>
    fromPromise(
      this.request(
        'getForm',
        {
          cuid,
          id,
        },
        {
          lang: language,
        }
      ),
      defaultForm
    )
  );

  @computed
  get completedFieldsCount() {
    return this.fields.reduce((acc, f) => {
      if (f.type === 'hidden' || f.isTextBlock) return acc;
      const value = this.getValue(f);
      if (requiredFieldIsEmpty(value)) {
        acc += 1;
      }
      return acc;
    }, 0);
  }

  @computed
  get totalFieldsCount() {
    return this.fields.reduce((acc, f) => {
      if (f.type !== 'hidden' && !f.isTextBlock) {
        acc += 1;
      }
      return acc;
    }, 0);
  }

  @computed
  get completion() {
    if (this.completedFieldsCount === 0) return 0;
    return Math.round(
      (this.completedFieldsCount / this.totalFieldsCount) * 100
    );
  }

  @computed
  get formPromise() {
    return this.cacheFormPromise(
      this.settingsStore.cuid,
      this.settingsStore.id,
      this.settingsStore.language,
      this.nonce && this.nonce.token
    );
  }

  @computed
  get form() {
    if (this.formPromise.state === 'rejected') return [];
    return this.formPromise.value;
    // to see and test the text fields:
    // const thing = this.formPromise.value;
    // thing.fields.push(
    //   {
    //     id: 12345,
    //     type: 'heading',
    //     name: 'The Bear: Diana'
    //   },
    //   {
    //     id: 12324,
    //     type: 'subheading',
    //     name: 'She had a problem.'
    //   },
    //   {
    //     id: 12398,
    //     type: 'info',
    //     name:
    //       'She only ate plums. Diana had a tough time as a bear who only ate plums. Eating plums was her sole joy. If she ate anything but, she would turn pink, then purple, then blue. She could be stuck in the rain, buried in mud, alone and without wifi, but if she had a basket of plums, well, Diana was pretty set. As a cub, her mother had traversed many a land to ensure that she was in an environment where plums were bountiful. Still, other bears gave her a tonne of shit for only eating plums. Sometimes, it made her sad, but mostly she was fine; she had her plums.'
    //   },
    //   {
    //     id: 34883,
    //     type: 'article',
    //     name: '1'
    //   }
    // );
    // return thing;
  }

  memoEmptyNonce = memoize(() => fromPromise(emptyPromise(), {}));
  // update memoized nonce fn when save attempt is incremented
  cacheNoncePromise = memoize(
    () => fromPromise(this.request('getNonce'), defaultNonce),
    () => this.saveAttempt
  );

  @computed
  get noncePromise() {
    if (!this.canFetchNonce) return this.memoEmptyNonce();
    return this.cacheNoncePromise();
  }

  @computed
  get nonce() {
    if (this.noncePromise.state === 'rejected') return '';
    return this.noncePromise.value;
  }

  @computed
  get isLoading() {
    return this.formPromise.state === 'pending';
  }

  @computed
  get isSaving() {
    return !!(
      this.saveAttempt &&
      (this.savePromise.state === 'pending' ||
        this.noncePromise.state === 'pending')
    );
  }

  @computed
  get hasError() {
    return this.formPromise.state === 'rejected';
  }

  @computed
  get saveError() {
    return (
      this.savePromise.state === 'rejected' ||
      this.noncePromise.state === 'rejected'
    );
  }

  selectText = f =>
    f.multiple ? defaultTexts.multiSelect : defaultTexts.singleSelect;

  @computed
  get fields() {
    if (this.formPromise.state === 'rejected') return [];
    const defaultOption = f => [
      {
        key: this.selectText(f),
        label: this.selectText(f),
        value: this.selectText(f),
        selected: true,
        disabled: true,
        className: 'defaultOption',
      },
    ];
    const options = f =>
      f.options.map(o => ({
        key: o.name,
        value: o.name,
        label: o.name,
        selected: o.selected,
      }));
    // if radio then options does not need the default option
    const totalOptions = f =>
      f.type === 'select' ? defaultOption(f).concat(options(f)) : options(f);

    return this.form.fields.map(f => {
      const isEmail = !!(f.type === 'email');
      return Object.assign(
        {},
        {
          isEmail,
          ...f,
          dataType:
            (f.type === 'select' && f.multiple) || f.type === 'checkbox'
              ? 'array'
              : 'string',
          options: f.options && f.options.length ? totalOptions(f) : [],
          isTextBlock: this.isInfoField(f.type),
        },
        { response: this.prefill(f, isEmail) },
        this.inputs.get(f.id) || null
      );
    });
  }

  @action
  prefill = (field, isEmail) => {
    const parsedValues = JSON.parse(this.storedValuesString);

    if (parsedValues.formValues[field.id]) {
      return parsedValues.formValues[field.id];
    }

    if (
      isEmail &&
      this.settingsStore &&
      this.settingsStore.user &&
      this.settingsStore.user.email
    ) {
      return this.settingsStore.user.email;
    }

    return '';
  };

  @computed
  get publicFields() {
    return this.fields.filter(f => f.type !== 'hidden');
  }

  isInfoField = type =>
    type === 'heading' ||
    type === 'info' ||
    type === 'subheading' ||
    type === 'article';

  @computed
  get validation() {
    const translations = this.settingsStore.locales.modules.customform;
    return this.fields.reduce(
      (acc, f) => ({
        ...acc,
        [f.id]: validateData(f, this.uploaderStore, translations),
      }),
      {}
    );
  }

  @computed
  get sendDebugInfo() {
    return this.form.sendDebugInfo;
  }

  @computed
  get addViaElevio() {
    return this.form.addViaElevio;
  }

  @action
  onChange = (data, id, isEmail) => {
    const response = {
      id,
      data,
      isEmail,
    };
    this.setJsonFormValue(response);
    this.addInput(response);
  };

  @action
  addInput = response => {
    const currentField = this.fields
      .slice()
      .filter(f => response.id === f.id)[0];
    // default response is required in the event that the user clears the select field
    const defaultResponse =
      currentField.multiple || currentField.type === 'checkbox' ? [] : '';

    currentField.response = response.data || defaultResponse;
    this.inputs.set(response.id, currentField);

    if (currentField.gatekeeperMapped) {
      this.gatekeeperValues[response.id] = currentField.response;
    }
  };

  @action
  setJsonFormValue = ({ data, id }) => {
    const parsedValues = JSON.parse(this.storedValuesString);
    parsedValues.formValues[id] = data;
    this.storedValuesString = JSON.stringify(parsedValues);
  };

  @action
  setJsonTicketUid = uid => {
    const parsedValues = JSON.parse(this.storedValuesString);
    parsedValues.ticketUid = uid;
    this.storedValuesString = JSON.stringify(parsedValues);
  };

  @action
  setValuesFromSession = storedValuesString => {
    this.storedValuesString = storedValuesString;
  };

  @action
  loadGatekeeperValuesFromSession = () => {
    const parsedValues = JSON.parse(this.storedValuesString);

    // Checks if there are stored values form sessionStorage
    const hasStoredValues =
      this.storedValuesString.length !== INITIAL_STORAGE_VALUES.length;

    if (!hasStoredValues && this.form.fields) {
      // Add formValues to parsedValues
      const formValues = {};
      toJS(this.form.fields).forEach(field => {
        formValues[field.id] = this.getValue(field);
      });
      parsedValues.formValues = formValues;
    }

    this.ticketUid = parsedValues.ticketUid;

    Object.keys(parsedValues.formValues).forEach(k => {
      const key = parseInt(k, 10);
      const currentField = this.form.fields
        .slice()
        .filter(f => key === f.id)[0];
      if (currentField && currentField.gatekeeperMapped) {
        this.gatekeeperValues[key] = parsedValues.formValues[key];
        this.oldGatekeeperValues[key] = parsedValues.formValues[key];
      }
    });
  };
  /* eslint-disable no-nested-ternary */
  @computed
  get buttonText() {
    const sendingOrSubmitText = this.isSaving
      ? this.settingsStore.locales.general.sending
      : this.settingsStore.locales.modules.customform.submit;

    const btnText = this.hasGateKeeperEnabled
      ? this.settingsStore.locales.general
          .continue /** NOTE: this.settingsStore.locales.general.continue is undefined on dev environment so if gatekeeper is enabled, the 'continue' button will be a blank '' */
      : sendingOrSubmitText;

    return btnText;
  }

  memoEmptySave = memoize(() => fromPromise(emptyPromise(), {}));
  // update memoized fn when nonce is updated
  cacheSave = memoize(
    (cuid, id, userHash) =>
      fromPromise(
        this.request(
          'postForm',
          {
            cuid,
            id,
          },
          {},
          this.formDataWithNonce,
          { 'user-hash': userHash }
        ),

        { success: '', message: '' }
      ),
    () => this.nonce && this.nonce.token
  );

  @computed
  get savePromise() {
    if (!this.canSave) return this.memoEmptySave();
    return this.cacheSave(
      this.settingsStore.cuid,
      this.settingsStore.id,
      this.settingsStore.user.userHash
    );
  }

  @computed
  get isSaved() {
    return !!(
      this.saveAttempt &&
      this.saveClicked &&
      this.savePromise.state === 'fulfilled' &&
      this.noncePromise.state === 'fulfilled'
    );
  }

  @computed
  get saveAttemptMade() {
    return this.saveClicked && !this.isSaved;
  }

  @computed
  get attemptsMatch() {
    return this.autorunForSaveAttempt === this.saveAttempt;
  }

  @computed
  get hasGatekeeperMappedValues() {
    const { form } = this;

    const jsForm = toJS(form);

    const gatekeeperFields = Object.keys(jsForm.fields)
      .map(fieldKey => jsForm.fields[fieldKey])
      .filter(field => field.gatekeeperMapped);
    const gatekeeperFieldsLength = gatekeeperFields.length;
    const hasMappedValues = !!gatekeeperFieldsLength;

    return hasMappedValues;
  }

  @computed
  get gatekeeperSearchValues() {
    return Object.keys(this.gatekeeperValues).reduce((acc, key) => {
      const value = this.gatekeeperValues[key];
      return acc.length ? `${acc}\n${value}` : value;
    }, '');
  }

  @action
  fetch = () => {
    this.fetching = true;
    this.searchedGatekeeperValues = cloneDeep(this.gatekeeperValues);
    const auth = authQuery(this.settingsStore.user);

    const url =
      this.settingsStore.parentSettings &&
      this.settingsStore.parentSettings.page_url;

    this.request(
      'suggestions',
      {
        cuid: this.settingsStore.cuid,
        lan: this.settingsStore.language,
        url: encodeURIComponent(url),
        auth,
      },
      {},
      {
        text: this.gatekeeperSearchValues,
      }
    )
      .then(res => {
        if (!res) return;
        if (res && res.results) {
          this.suggestions = res.results;
        }
        this.searchKeywords = res.search_keywords;
      })
      .catch(error => {
        reportError('Failed to fetch suggestions', error, {
          text: this.gatekeeperSearchValues,
        });
        this.suggestionsError = error;
      })
      .finally(() => {
        this.fetching = false;
      });
  };

  @action
  submitPressed = () => {
    const searchChanged = !isEqual(
      this.gatekeeperValues,
      this.oldGatekeeperValues
    );

    if (!this.ticketUid || searchChanged) {
      const uid = uuidv4();
      this.ticketUid = uid;
      this.setJsonTicketUid(uid);
      this.oldGatekeeperValues = cloneDeep(this.gatekeeperValues);
    }
  };

  @action
  setArticleHelped = () => {
    this.articleHelped = true;
  };

  @computed
  get isReset() {
    return !(this.ticketUid && !isEmpty(this.gatekeeperValues));
  }

  // to add notifications about file uploads
  @computed
  get notifications() {
    const notifs = [];
    if (this.saveError) {
      notifs.push({
        text: this.settingsStore.locales.general.error,
        type: 'error',
      });
    }
    if (this.saveAttemptMade && !this.isValid) {
      notifs.push({
        text: this.settingsStore.locales.modules.customform.complete_fields,
        type: 'error',
      });
    }
    return notifs.length > 0 ? notifs : [];
  }

  @computed
  get isValid() {
    return every(values(this.validation), v => !!v.isValidField);
  }

  @computed
  get isValidUi() {
    if (!this.submitClicked) {
      return mapValues(this.validation, () => ({
        isValidField: true,
        message: '',
      }));
    }
    return this.validation;
  }

  @computed
  get append() {
    const debug = debugInfo(
      this.settingsStore.parentSettings.page_url,
      this.settingsStore.user
    );
    return [
      {
        append: `\n\n\n======== debug info ========\n\nVia: elevio\n\n${
          debug.body
        }`,
        debug: debug.debug,
      },
    ];
  }

  @computed
  get traits() {
    return [{ traits: toJS(this.settingsStore.user.traits) }];
  }

  // if multiple is true then f.response is an array
  // if multiple is false then f.response is a string
  selectValue = f =>
    f.response.length > 1 ? f.response.slice().join(', ') : f.response[0];

  getValue = f => {
    if (f.type === 'select' && f.multiple) {
      return this.selectValue(f);
    } else if (
      f.type === 'upload' &&
      this.uploaderStore.getFiles &&
      this.uploaderStore.getFiles(f.id)
    ) {
      return this.uploaderStore
        .getFiles(f.id)
        .fileList.map(file => file.location);
    }
    return f.value || f.response || '';
  };

  @computed
  get formData() {
    let data = this.fields.reduce(
      (acc, f) =>
        f.isTextBlock
          ? acc
          : acc.concat({
              name: `field_${f.id}`,
              value: this.getValue(f),
            }),
      []
    );
    const { language } = this.settingsStore;
    const { user } = this.settingsStore;

    if (user.authenticated) {
      // delete user.userHash;
      const userData = {
        user: { ...user, userHash: undefined },
      };
      data = data.concat(userData);
    }
    return this.sendDebugInfo
      ? data.concat({ language }, this.append, this.traits)
      : data.concat({ language });
  }

  @computed
  get formDataWithNonce() {
    const nonce = this.nonce && this.nonce.token;
    return this.formData.concat({ nonce });
  }

  @action
  save = () => {
    this.submitClicked = true;
    if (!this.isValid) return;
    this.saveClicked = true;
    this.saveAttempt += 1;
    // this will kick off the saving process in the autorun.
  };

  updateUiOnSave = () =>
    autorun(() => {
      if (this.isSaved && !this.attemptsMatch) {
        this.autorunForSaveAttempt = this.saveAttempt;
        trackTicketSubmit(this.settingsStore.trackEvent, this.ticketUid);

        setTimeout(() => {
          this.settingsStore.child.send('storage-remove', {
            key: 'form',
          });
        }, 1250);
      }
    });

  @computed
  get hasGateKeeperEnabled() {
    const {
      form: { gatekeeperEnabled },
      hasGatekeeperMappedValues,
    } = this;
    const isGateKeeperEnabled = gatekeeperEnabled && hasGatekeeperMappedValues;

    return isGateKeeperEnabled;
  }

  @computed
  get shouldSearch() {
    return !isEqual(this.gatekeeperValues, this.searchedGatekeeperValues);
  }

  @action
  reset = () => {
    this.saving = false;
    this.saveClicked = false;
    this.submitClicked = false;
    this.autorunForSaveAttempt = 0;
    this.searchedGatekeeperValues = {};
    this.gatekeeperValues = {};
    this.oldGatekeeperValues = {};
    this.storedValuesString = INITIAL_STORAGE_VALUES;
    this.suggestions = [];
    this.searchKeywords = [];
    this.ticketUid = undefined;
    this.suggestionsError = null;
    this.inputs.clear();
    this.uploaderStore.removeAllFiles();
    this.sent = false;
    this.articleHelped = false;
    this.fetching = undefined;
  };

  @action
  setSent = () => {
    this.sent = true;
  };
}

const settingsStore = new SettingsStore();
const fetcher = fetch({ API_URL, RESOURCES }, settingsStore);
const uploaderStore = new UploaderStore(settingsStore, fetcher);
const ObservableForm = new Form(settingsStore, fetcher, uploaderStore);

ObservableForm.storage().then(storage => {
  const storageSupported = isStorageSupported();
  const hydrate = !storageSupported
    ? () => {}
    : create({
        storage: {
          getItem: () =>
            new Promise(resolve => {
              resolve(storage);
            }),
          setItem: (key, data) => {
            if (key === 'form') {
              if (!data.storedValuesString) {
                settingsStore.child.send('storage-remove', {
                  key: 'form',
                });
                return;
              }
            }

            settingsStore.child.send('storage-put', {
              key,
              data,
            });
          },
        },
        debounce: 2000,
        jsonify: false,
      });

  hydrate('form', ObservableForm);
});

export default ObservableForm;



// WEBPACK FOOTER //
// ./src/stores/form.js